import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../config/api';
import { getHostedAppUrls } from '../config/hostUrls';
import {
  abortDismissalSimulation,
  DEFAULT_SIMULATION_SPEED,
  getStudentIdsByParentId,
  loadDismissalSimulationSeed,
  resetDismissalSimulation,
  startDismissalSimulation,
} from '../lib/dismissalSimulation';

const PHASE_LABELS = {
  idle: 'Ready to Start',
  arriving: 'Parents Arriving',
  calling: 'Calling Students',
  sending: 'Sending to Carpool',
  complete: 'Dismissal Complete',
};

const STATUS_COLORS = {
  waiting: '#94a3b8',
  arriving: '#f59e0b',
  called: '#3b82f6',
  sent: '#22c55e',
  pickedUp: '#8b5cf6',
};

const STATUS_LABELS = {
  waiting: 'In Class',
  arriving: 'Requested',
  called: 'Escorted',
  sent: 'At Curb',
  pickedUp: 'Picked Up',
};

const MONITOR_NAMES = ['Monitor A', 'Monitor B', 'Monitor C'];
const STAFF_NAMES = ['Release A', 'Release B', 'Release C'];
const CAR_STATE_LABELS = {
  scheduled: 'Scheduled',
  approaching: 'Approaching Gate',
  waiting: 'Queued in Geofence',
  loading: 'Loading Students',
  completed: 'Departed',
};

const APP_OVERVIEW_FRAMES = [
  { id: 'staff-a', role: 'staff', label: 'Staff View A' },
  { id: 'staff-b', role: 'staff', label: 'Staff View B' },
  { id: 'staff-c', role: 'staff', label: 'Staff View C' },
  { id: 'parent-a', role: 'parent', label: 'Parent View A' },
  { id: 'parent-b', role: 'parent', label: 'Parent View B' },
];

function getAppOverviewOrigins() {
  const { parent, staff } = getHostedAppUrls();
  return {
    staff,
    parent,
  };
}

function getGradeLabel(grade) {
  if (grade === null || grade === undefined || grade === '') return 'Unassigned';
  return String(grade).trim();
}

function compareGradeLabels(left, right) {
  const leftValue = Number(left);
  const rightValue = Number(right);
  const leftIsNumber = !Number.isNaN(leftValue);
  const rightIsNumber = !Number.isNaN(rightValue);

  if (leftIsNumber && rightIsNumber) return leftValue - rightValue;
  if (leftIsNumber) return -1;
  if (rightIsNumber) return 1;
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

function distributeAcross(items, count) {
  const buckets = Array.from({ length: count }, () => []);
  items.forEach((item, index) => {
    buckets[index % count].push(item);
  });
  return buckets;
}

function groupStudentsByGrade(entries) {
  const grouped = new Map();

  entries.forEach((entry) => {
    const key = getGradeLabel(entry.grade);
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(entry);
  });

  return [...grouped.entries()]
    .sort(([leftGrade], [rightGrade]) => compareGradeLabels(leftGrade, rightGrade))
    .map(([grade, students]) => ({
      grade,
      students: students.sort((left, right) => left.studentName.localeCompare(right.studentName)),
    }));
}

function buildBlueprintScene(entries, phase) {
  const parentGroups = new Map();

  entries.forEach((entry) => {
    const key = entry.parentId ? `parent-${entry.parentId}` : `student-${entry.id}`;
    if (!parentGroups.has(key)) {
      parentGroups.set(key, {
        key,
        parentId: entry.parentId,
        parentName: entry.parentName,
        students: [],
      });
    }
    parentGroups.get(key).students.push(entry);
  });

  const cars = [...parentGroups.values()]
    .map((group) => {
      const counts = { waiting: 0, arriving: 0, called: 0, sent: 0, pickedUp: 0 };
      group.students.forEach((student) => {
        counts[student.status] = (counts[student.status] || 0) + 1;
      });

      let state = 'scheduled';
      if (group.students.every((student) => student.status === 'pickedUp')) {
        state = 'completed';
      } else if (group.students.some((student) => student.status === 'sent')) {
        state = 'loading';
      } else if (group.students.some((student) => student.status === 'called')) {
        state = 'waiting';
      } else if (group.students.some((student) => student.status === 'arriving')) {
        state = 'approaching';
      } else if (phase !== 'idle') {
        state = 'scheduled';
      }

      return {
        ...group,
        counts,
        state,
        requestedStudents: group.students.filter((student) => student.status === 'arriving'),
        loadingStudents: group.students.filter((student) => student.status === 'sent'),
      };
    })
    .sort((left, right) => left.parentName.localeCompare(right.parentName));

  const classroomStudents = entries.filter((entry) => ['waiting', 'arriving'].includes(entry.status));
  const calledStudents = entries.filter((entry) => entry.status === 'called');
  const escortCount = calledStudents.length > 1 ? Math.ceil(calledStudents.length * 0.5) : calledStudents.length;
  const escortStudents = calledStudents.slice(0, escortCount);
  const holdingStudents = calledStudents.slice(escortCount);
  const monitorGroups = distributeAcross(escortStudents, MONITOR_NAMES.length).map((students, index) => ({
    id: MONITOR_NAMES[index],
    students,
  }));
  const queuedCars = cars.filter((car) => ['approaching', 'waiting'].includes(car.state));
  const curbCars = cars.filter((car) => car.state === 'loading');
  const departedCars = cars.filter((car) => car.state === 'completed');
  const staffAssignments = distributeAcross(curbCars, STAFF_NAMES.length).map((carsForStaff, index) => ({
    id: STAFF_NAMES[index],
    cars: carsForStaff,
  }));

  return {
    classroomGroups: groupStudentsByGrade(classroomStudents),
    requestedStudents: classroomStudents.filter((entry) => entry.status === 'arriving'),
    monitorGroups,
    holdingStudents,
    queuedCars,
    curbCars,
    departedCars,
    staffAssignments,
  };
}

function getChipStyle(status) {
  const color = STATUS_COLORS[status] || '#94a3b8';
  return {
    borderColor: color,
    background: `${color}14`,
    boxShadow: `inset 0 0 0 1px ${color}26`,
  };
}

export default function Simulate({ token, navigate }) {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [carpoolLines, setCarpoolLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [simState, setSimState] = useState({}); // { studentId: { status, parentId, parentName } }
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState(DEFAULT_SIMULATION_SPEED);
  const [phase, setPhase] = useState('idle');
  const [frameReloadSeed, setFrameReloadSeed] = useState(0);

  // Load schools
  useEffect(() => {
    apiFetch('/schools', {}, token).then((res) => setSchools(res.data || [])).catch(() => {});
  }, [token]);

  const addLog = useCallback((msg, type = 'info') => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLog((prev) => [{ time, msg, type }, ...prev].slice(0, 200));
  }, []);

  // Load school data
  const loadSchool = async () => {
    if (!schoolId) return;
    setLoading(true);
    setSimState({});
    setLog([]);
    setPhase('idle');
    try {
      const seed = await loadDismissalSimulationSeed({ schoolId, token });
      setCarpoolLines(seed.carpoolLines);
      setSimState(seed.simState);
      addLog(
        `Loaded ${seed.studentCount} students, ${seed.pickupContactCount} pickup contacts, ${seed.carpoolLines.length} carpool lines${seed.skippedStudentCount ? `, skipped ${seed.skippedStudentCount} unmatched students` : ''}`
      );
    } catch (err) {
      addLog(`Error loading school data: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getSiblingIds = useCallback((studentId, state = simState) => {
    const parentId = Number(state[studentId]?.parentId);
    if (!parentId) return [Number(studentId)];
    return getStudentIdsByParentId(state).get(parentId) || [Number(studentId)];
  }, [simState]);

  const canParentExit = useCallback((studentId, state = simState) => {
    const siblingIds = getSiblingIds(studentId, state);
    return siblingIds.every((siblingId) => ['sent', 'pickedUp'].includes(state[siblingId]?.status));
  }, [getSiblingIds, simState]);

  const getCarpoolLineIdForStudents = useCallback((studentIds = [], state = simState) => (
    studentIds
      .map((id) => state[id]?.carpoolLineId)
      .map(Number)
      .find((lineId) => Number.isInteger(lineId) && lineId > 0)
    || carpoolLines[0]?.carpool_line_id
    || null
  ), [carpoolLines, simState]);

  // Simulate a single parent entering geofence
  const simParentArrive = async (studentId) => {
    const s = simState[studentId];
    if (!s) return;
    const siblingIds = getSiblingIds(studentId);
    try {
      if (s.parentId) {
        await apiFetch('/geofenced-data', {
          method: 'POST',
          body: JSON.stringify({
            parent_driver_id: s.parentId,
            school_id: parseInt(schoolId),
            action: 'enter',
            timestamp: new Date().toISOString(),
            carpool_line_id: getCarpoolLineIdForStudents(siblingIds),
          }),
        }, token);
      }
      setSimState((prev) => {
        const next = { ...prev };
        siblingIds.forEach((siblingId) => {
          if (next[siblingId]) {
            next[siblingId] = { ...next[siblingId], status: 'arriving' };
          }
        });
        return next;
      });
      addLog(
        `🚗 ${s.parentName} entered geofence (${siblingIds.length === 1 ? `child: ${s.studentName}` : `${siblingIds.length} students`})`,
        'arrive'
      );
    } catch (err) {
      addLog(`Failed geofence enter for ${s.parentName}: ${err.message}`, 'error');
    }
  };

  // Simulate teacher calling student
  const simCallStudent = async (studentId) => {
    const s = simState[studentId];
    if (!s) return;
    try {
      await apiFetch('/dismissal-status', {
        method: 'PUT',
        body: JSON.stringify({ student_id: studentId, school_id: parseInt(schoolId), status: 'called' }),
      }, token);
      setSimState((prev) => ({ ...prev, [studentId]: { ...prev[studentId], status: 'called' } }));
      addLog(`📢 ${s.studentName} called to carpool`, 'call');
    } catch (err) {
      addLog(`Failed to call ${s.studentName}: ${err.message}`, 'error');
    }
  };

  // Simulate student sent to carpool
  const simSendStudent = async (studentId) => {
    const s = simState[studentId];
    if (!s) return;
    try {
      await apiFetch('/dismissal-status', {
        method: 'PUT',
        body: JSON.stringify({ student_id: studentId, school_id: parseInt(schoolId), status: 'sent' }),
      }, token);
      setSimState((prev) => ({ ...prev, [studentId]: { ...prev[studentId], status: 'sent' } }));
      addLog(`✅ ${s.studentName} sent to carpool`, 'send');
    } catch (err) {
      addLog(`Failed to send ${s.studentName}: ${err.message}`, 'error');
    }
  };

  // Simulate parent leaving with student
  const simParentLeave = async (studentId) => {
    const s = simState[studentId];
    if (!s) return;
    const siblingIds = getSiblingIds(studentId);

    if (!canParentExit(studentId)) {
      addLog(`Cannot complete pickup for ${s.parentName} until all linked students are sent`, 'error');
      return;
    }

    try {
      const sentSiblingIds = siblingIds.filter((siblingId) => simState[siblingId]?.status === 'sent');
      for (const siblingId of sentSiblingIds) {
        // eslint-disable-next-line no-await-in-loop
        await apiFetch('/dismissal-status', {
          method: 'PUT',
          body: JSON.stringify({ student_id: siblingId, school_id: parseInt(schoolId), status: 'picked_up' }),
        }, token);
      }

      if (s.parentId) {
        await apiFetch('/geofenced-data', {
          method: 'POST',
          body: JSON.stringify({
            parent_driver_id: s.parentId,
            school_id: parseInt(schoolId),
            action: 'exit',
            timestamp: new Date().toISOString(),
            carpool_line_id: getCarpoolLineIdForStudents(siblingIds),
          }),
        }, token);
      }
      setSimState((prev) => {
        const next = { ...prev };
        siblingIds.forEach((siblingId) => {
          if (next[siblingId] && next[siblingId].status === 'sent') {
            next[siblingId] = { ...next[siblingId], status: 'pickedUp' };
          }
        });
        return next;
      });
      addLog(
        `🏁 ${s.parentName} left with ${siblingIds.map((siblingId) => simState[siblingId]?.studentName).filter(Boolean).join(', ')}`,
        'pickup'
      );
    } catch (err) {
      addLog(`Failed geofence exit for ${s.parentName}: ${err.message}`, 'error');
    }
  };

  // Run full auto simulation
  const runSimulation = async () => {
    setRunning(true);
    try {
      await startDismissalSimulation({
        schoolId,
        token,
        speed,
        simState,
        carpoolLines,
        onPhaseChange: setPhase,
        onLog: addLog,
        onStatusChange: (studentId, nextStatus) => {
          setSimState((prev) => {
            if (!prev[studentId]) return prev;
            return {
              ...prev,
              [studentId]: {
                ...prev[studentId],
                status: nextStatus,
              },
            };
          });
        },
      });
    } finally {
      setRunning(false);
    }
  };

  const stopSimulation = async () => {
    await abortDismissalSimulation({ schoolId });
    setRunning(false);
    addLog('Simulation stopped by user', 'phase');
  };

  const resetSimulation = async () => {
    await abortDismissalSimulation({ schoolId });
    setRunning(false);
    setPhase('idle');
    setSimState((prev) => {
      const reset = {};
      Object.keys(prev).forEach((id) => { reset[id] = { ...prev[id], status: 'waiting' }; });
      return reset;
    });

    // Reset dismissal statuses in the database so all apps see students back in "waiting"
    try {
      await resetDismissalSimulation({ schoolId, token });
      addLog('Simulation reset — all statuses cleared', 'phase');
    } catch (err) {
      addLog(`Reset API error: ${err.message}`, 'error');
    }
  };

  // Counts
  const counts = { waiting: 0, arriving: 0, called: 0, sent: 0, pickedUp: 0 };
  Object.values(simState).forEach((s) => { counts[s.status] = (counts[s.status] || 0) + 1; });
  const total = Object.keys(simState).length;
  const studentEntries = Object.entries(simState).map(([id, entry]) => ({ id: Number(id), ...entry }));
  const blueprint = buildBlueprintScene(studentEntries, phase);
  const appOverviewOrigins = getAppOverviewOrigins();
  const appOverviewFrames = APP_OVERVIEW_FRAMES.map((frame) => ({
    ...frame,
    src: frame.role === 'staff' ? appOverviewOrigins.staff : appOverviewOrigins.parent,
  }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="sa-btn" onClick={() => navigate('dashboard')}>← Back</button>
        <h2 style={{ margin: 0 }}>Dismissal Simulation</h2>
      </div>

      {/* School selector */}
      <div className="sa-card sim-setup-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Setup</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="sa-field" style={{ minWidth: 250 }}>
            <label>School</label>
            <select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="sa-input">
              <option value="">Select a school…</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>{s.school_nickname || s.legal_name}</option>
              ))}
            </select>
          </div>
          <button className="sa-btn sa-btn-primary" onClick={loadSchool} disabled={!schoolId || loading}>
            {loading ? 'Loading…' : 'Load School'}
          </button>
          <div className="sa-field" style={{ minWidth: 180 }}>
            <label>Speed (ms between actions)</label>
            <input type="range" min={200} max={3000} step={100} value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))} style={{ width: '100%' }} />
            <span style={{ fontSize: 12, color: '#666' }}>{speed}ms</span>
          </div>
        </div>
      </div>

      <div className="sa-card sim-iframe-card" style={{ marginBottom: 16 }}>
        <div className="sim-iframe-header">
          <div>
            <h3 style={{ margin: 0 }}>Live App Overview</h3>
            <p className="sim-iframe-subtitle">
              Mobile-sized staff and parent web sessions in one place. Each frame is an isolated in-memory app instance,
              so you can log into different accounts side by side without changing the rest of the simulation page.
            </p>
          </div>
          <div className="sim-iframe-actions">
            <button className="sa-btn sa-btn-secondary" onClick={() => setFrameReloadSeed((value) => value + 1)}>
              Reload All Frames
            </button>
            <a className="sim-iframe-link" href={appOverviewOrigins.staff} target="_blank" rel="noreferrer">Open Staff App</a>
            <a className="sim-iframe-link" href={appOverviewOrigins.parent} target="_blank" rel="noreferrer">Open Parent App</a>
          </div>
        </div>

        <div className="sim-iframe-grid">
          {appOverviewFrames.map((frame) => (
            <section key={frame.id} className="sim-device-card">
              <div className="sim-device-header">
                <div className="sim-device-meta">
                  <span className={`sim-device-role sim-device-role-${frame.role}`}>{frame.role}</span>
                  <strong>{frame.label}</strong>
                </div>
                <a className="sim-device-open" href={frame.src} target="_blank" rel="noreferrer">Open</a>
              </div>

              <div className="sim-device-shell">
                <div className="sim-device-notch" />
                <iframe
                  key={`${frame.id}-${frameReloadSeed}`}
                  title={frame.label}
                  src={frame.src}
                  className="sim-device-frame"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </section>
          ))}
        </div>
      </div>

      {total > 0 && (
        <>
          {/* Controls & Status Bar */}
          <div className="sa-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {!running ? (
                  <button className="sa-btn sa-btn-primary" onClick={runSimulation} disabled={phase === 'complete'}>
                    ▶ Run Full Simulation
                  </button>
                ) : (
                  <button className="sa-btn" style={{ background: '#dc2626', color: '#fff' }} onClick={stopSimulation}>
                    ⏹ Stop
                  </button>
                )}
                <button className="sa-btn" onClick={resetSimulation}>↻ Reset</button>
              </div>

              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                <span style={{ fontWeight: 700 }}>Phase: {PHASE_LABELS[phase]}</span>
                <span>Progress: {counts.pickedUp}/{total}</span>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ marginTop: 12, background: '#e5e7eb', borderRadius: 8, height: 24, overflow: 'hidden', display: 'flex' }}>
              {Object.entries(STATUS_COLORS).map(([status, color]) => {
                const pct = total > 0 ? (counts[status] / total) * 100 : 0;
                return pct > 0 ? (
                  <div key={status} style={{ width: `${pct}%`, background: color, height: '100%', transition: 'width 0.3s' }}
                    title={`${status}: ${counts[status]}`} />
                ) : null;
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_COLORS).map(([status, color]) => (
                <span key={status} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
                  {status} ({counts[status]})
                </span>
              ))}
            </div>
          </div>

          <div className="sa-card sim-blueprint-card">
            <div className="sim-blueprint-header">
              <div>
                <h3 style={{ margin: 0 }}>Campus Blueprint</h3>
                <p className="sim-blueprint-subtitle">
                  Top-down view of classrooms, hall-monitor flow, holding space, and the pickup loop around the school.
                </p>
              </div>
              <div className="sim-blueprint-phase-badge">
                <span className="sim-blueprint-phase-label">Live Phase</span>
                <strong>{PHASE_LABELS[phase]}</strong>
              </div>
            </div>

            <div className="sim-blueprint-summary">
              <div className="sim-summary-chip">
                <span className="sim-summary-value">{counts.waiting + counts.arriving}</span>
                <span className="sim-summary-label">Students in classrooms</span>
              </div>
              <div className="sim-summary-chip">
                <span className="sim-summary-value">{counts.called}</span>
                <span className="sim-summary-label">In hall-monitor flow</span>
              </div>
              <div className="sim-summary-chip">
                <span className="sim-summary-value">{counts.sent}</span>
                <span className="sim-summary-label">At pickup curb</span>
              </div>
              <div className="sim-summary-chip">
                <span className="sim-summary-value">{counts.pickedUp}</span>
                <span className="sim-summary-label">Completed pickups</span>
              </div>
            </div>

            <div className="sim-blueprint-board">
              <section className="sim-zone sim-zone-school">
                <div className="sim-zone-header">
                  <div>
                    <span className="sim-zone-kicker">Inside School</span>
                    <h4>Classrooms</h4>
                  </div>
                  <span className="sim-zone-count">{counts.waiting + counts.arriving} students</span>
                </div>
                <div className="sim-classroom-grid">
                  {blueprint.classroomGroups.length === 0 ? (
                    <div className="sim-zone-empty">No students remain in classrooms.</div>
                  ) : blueprint.classroomGroups.map((group) => (
                    <div key={group.grade} className="sim-classroom-card">
                      <div className="sim-classroom-title">
                        <strong>Grade {group.grade}</strong>
                        <span>{group.students.length} waiting</span>
                      </div>
                      <div className="sim-student-stack">
                        {group.students.map((student) => (
                          <span
                            key={student.id}
                            className="sim-student-chip"
                            style={getChipStyle(student.status)}
                            title={`${student.studentName} · ${STATUS_LABELS[student.status]} · ${student.parentName}`}
                          >
                            <span className="sim-student-name">{student.studentName}</span>
                            <span className="sim-student-meta">{student.status === 'arriving' ? 'Parent at gate' : 'Awaiting call'}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="sim-zone sim-zone-road">
                <div className="sim-zone-header">
                  <div>
                    <span className="sim-zone-kicker">Outside School</span>
                    <h4>Geofenced Pickup Loop</h4>
                  </div>
                  <span className="sim-zone-count">{blueprint.queuedCars.length + blueprint.departedCars.length} vehicles</span>
                </div>
                <div className="sim-geofence-shell">
                  <div className="sim-geofence-label">Geofence Active Zone</div>
                  <div className="sim-car-lane">
                    {blueprint.queuedCars.length === 0 ? (
                      <div className="sim-zone-empty">No cars in the geofence yet.</div>
                    ) : blueprint.queuedCars.map((car) => (
                      <div key={car.key} className={`sim-car-card sim-car-${car.state}`}>
                        <div className="sim-car-title">
                          <strong>{car.parentName}</strong>
                          <span>{CAR_STATE_LABELS[car.state]}</span>
                        </div>
                        <div className="sim-car-students">
                          {car.students.map((student) => (
                            <span key={student.id} className="sim-mini-chip" style={getChipStyle(student.status)}>
                              {student.studentName}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sim-departed-strip">
                  <span className="sim-zone-kicker">Completed</span>
                  <div className="sim-departed-list">
                    {blueprint.departedCars.length === 0 ? (
                      <span className="sim-inline-empty">No cars have exited yet.</span>
                    ) : blueprint.departedCars.map((car) => (
                      <span key={car.key} className="sim-departed-pill">
                        {car.parentName} · {car.students.length} student{car.students.length === 1 ? '' : 's'}
                      </span>
                    ))}
                  </div>
                </div>
              </section>

              <section className="sim-zone sim-zone-monitors">
                <div className="sim-zone-header">
                  <div>
                    <span className="sim-zone-kicker">Transit</span>
                    <h4>Hall Monitors</h4>
                  </div>
                  <span className="sim-zone-count">{counts.called} active calls</span>
                </div>
                <div className="sim-monitor-list">
                  {blueprint.monitorGroups.map((monitor) => (
                    <div key={monitor.id} className="sim-monitor-card">
                      <div className="sim-monitor-title">
                        <strong>{monitor.id}</strong>
                        <span>{monitor.students.length ? 'Escorting to holding' : 'Standing by'}</span>
                      </div>
                      <div className="sim-student-stack">
                        {monitor.students.length === 0 ? (
                          <div className="sim-zone-empty sim-zone-empty-tight">No current pickups.</div>
                        ) : monitor.students.map((student) => (
                          <span key={student.id} className="sim-student-chip" style={getChipStyle(student.status)}>
                            <span className="sim-student-name">{student.studentName}</span>
                            <span className="sim-student-meta">{student.parentName}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="sim-zone sim-zone-holding">
                <div className="sim-zone-header">
                  <div>
                    <span className="sim-zone-kicker">Staging</span>
                    <h4>Holding Area</h4>
                  </div>
                  <span className="sim-zone-count">{blueprint.holdingStudents.length} students grouped</span>
                </div>
                <div className="sim-student-stack">
                  {blueprint.holdingStudents.length === 0 ? (
                    <div className="sim-zone-empty">Students move here after monitors collect them.</div>
                  ) : blueprint.holdingStudents.map((student) => (
                    <span key={student.id} className="sim-student-chip" style={getChipStyle(student.status)}>
                      <span className="sim-student-name">{student.studentName}</span>
                      <span className="sim-student-meta">Queued for {student.parentName}</span>
                    </span>
                  ))}
                </div>
              </section>

              <section className="sim-zone sim-zone-curb">
                <div className="sim-zone-header">
                  <div>
                    <span className="sim-zone-kicker">Release Point</span>
                    <h4>Staff at Pickup Curb</h4>
                  </div>
                  <span className="sim-zone-count">{counts.sent} students assigned</span>
                </div>
                <div className="sim-staff-grid">
                  {blueprint.staffAssignments.map((assignment) => (
                    <div key={assignment.id} className="sim-staff-card">
                      <div className="sim-monitor-title">
                        <strong>{assignment.id}</strong>
                        <span>{assignment.cars.length ? 'Matching students to cars' : 'Waiting for next vehicle'}</span>
                      </div>
                      <div className="sim-curb-car-list">
                        {assignment.cars.length === 0 ? (
                          <div className="sim-zone-empty sim-zone-empty-tight">No cars at this lane.</div>
                        ) : assignment.cars.map((car) => (
                          <div key={car.key} className="sim-curb-car-card">
                            <div className="sim-car-title">
                              <strong>{car.parentName}</strong>
                              <span>{CAR_STATE_LABELS[car.state]}</span>
                            </div>
                            <div className="sim-car-students">
                              {car.loadingStudents.map((student) => (
                                <span key={student.id} className="sim-mini-chip" style={getChipStyle(student.status)}>
                                  {student.studentName}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>

          {/* Main area: Student grid + Log */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 16 }}>
            {/* Student grid */}
            <div className="sa-card" style={{ maxHeight: 500, overflow: 'auto' }}>
              <h3 style={{ marginTop: 0 }}>Students ({total})</h3>
              <table className="sa-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Grade</th>
                    <th>Parent</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(simState).map(([id, s]) => (
                    <tr key={id}>
                      <td>{s.studentName}</td>
                      <td>{s.grade}</td>
                      <td style={{ fontSize: 12, color: s.parentId ? '#333' : '#999' }}>{s.parentName}</td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11,
                          fontWeight: 600, color: '#fff', background: STATUS_COLORS[s.status],
                        }}>
                          {s.status}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {s.status === 'waiting' && (
                          <button className="sa-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => simParentArrive(Number(id))} disabled={running}>Arrive</button>
                        )}
                        {s.status === 'arriving' && (
                          <button className="sa-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => simCallStudent(Number(id))} disabled={running}>Call</button>
                        )}
                        {s.status === 'called' && (
                          <button className="sa-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => simSendStudent(Number(id))} disabled={running}>Send</button>
                        )}
                        {s.status === 'sent' && canParentExit(Number(id)) && (
                          <button className="sa-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => simParentLeave(Number(id))} disabled={running}>Pickup</button>
                        )}
                        {s.status === 'sent' && !canParentExit(Number(id)) && (
                          <span style={{ fontSize: 11, color: '#888' }}>Waiting for siblings</span>
                        )}
                        {s.status === 'pickedUp' && (
                          <span style={{ fontSize: 11, color: '#888' }}>Done</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Event log */}
            <div className="sa-card" style={{ maxHeight: 500, overflow: 'auto' }}>
              <h3 style={{ marginTop: 0 }}>Event Log</h3>
              <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                {log.length === 0 ? (
                  <p style={{ color: '#999' }}>Events will appear here…</p>
                ) : log.map((entry, i) => (
                  <div key={i} style={{
                    color: entry.type === 'error' ? '#dc2626'
                      : entry.type === 'phase' ? '#7c3aed'
                      : '#333',
                    fontWeight: entry.type === 'phase' ? 700 : 400,
                    borderBottom: entry.type === 'phase' ? '1px solid #e5e7eb' : 'none',
                    paddingBottom: entry.type === 'phase' ? 4 : 0,
                    marginBottom: entry.type === 'phase' ? 4 : 0,
                  }}>
                    <span style={{ color: '#999' }}>{entry.time}</span> {entry.msg}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
